import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaPalette,
  FaEye,
  FaEnvelope,
  FaLock,
  FaInfoCircle,
  FaUpload,
  FaArrowLeft,
  FaEdit,
  FaUndo,
  FaTimes,
} from 'react-icons/fa';
import axios from 'axios';
import { graphqlRequest } from '../utils/graphqlRequest';
import { applyBrandingFromConfig } from '../utils/tenantBranding';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';

// ─── Persistence ──────────────────────────────────────────────────────────────
// White-label branding is persisted per-tenant via the backend
// getWhiteLabelConfig / updateWhiteLabelConfig GraphQL API. Defaults below fill
// any section the tenant hasn't customized yet.

const GET_WHITE_LABEL = `
  query GetWhiteLabelConfig {
    getWhiteLabelConfig {
      id
      brand
      email
      login
    }
  }
`;

const UPDATE_WHITE_LABEL = `
  mutation UpdateWhiteLabelConfig($input: UpdateWhiteLabelInput!) {
    updateWhiteLabelConfig(input: $input) {
      id
      brand
      email
      login
    }
  }
`;

const GENERATE_UPLOAD_URL = `
  mutation GenerateUrlForImageUpload($files: [Fileupload!]!) {
    generateUrlForImageUploadClient(files: $files) {
      key
      url
    }
  }
`;

// Build a public S3 URL from a stored key (mirrors SettingsHub.resolveUploadUrl).
// Returns '' when the value can't be resolved to a usable URL.
function resolveUploadUrl(rawUpload) {
  if (!rawUpload) return '';
  const value = String(rawUpload).trim();
  if (!value) return '';
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
  if (value.startsWith('/')) return `${window.location.origin}${value}`;
  const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
  const region = process.env.REACT_APP_AWS_REGION;
  if (bucket && region) {
    return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
  }
  return '';
}

const DEFAULTS = {
  brand: {
    brandName: 'Your Company',
    tagline: 'Empowering Your Success',
    primaryLogo: '',
    darkLogo: '',
    favicon: '',
    primaryColor: '#7c3aed',
    secondaryColor: '#6b5cf6',
    accentColor: '#a78bfa',
    removeSaasName: false,
    removeSaasLogo: false,
    hidePoweredBy: false,
  },
  email: {
    emailLogo: '',
    headerColor: '#7C3AED',
    footerText: '© 2026 Your Company. All rights reserved.',
    supportEmail: 'support@yourcompany.com',
    contactNumber: '+1 (555) 123-4567',
    address: '',
    fromName: 'Your Company CRM',
    fromEmail: 'noreply@yourcompany.com',
    replyTo: '',
    bcc: '',
    templates: {},
  },
  login: {
    bgImage: '',
    bgColor: '#F3F4F6',
    headline: 'Welcome Back',
    subtext: 'Sign in to your account to continue',
    buttonLabel: 'Sign In',
    enableCustomCss: false,
    customCss: '',
    showSocialLogin: false,
    enable2fa: false,
    showLanguage: true,
    customTerms: false,
  },
};

const EMAIL_TEMPLATES = [
  { name: 'Invoice Sent', lastModified: 'Feb 28, 2026' },
  { name: 'Deal Won', lastModified: 'Feb 25, 2026' },
  { name: 'SLA Breach', lastModified: 'Feb 20, 2026' },
  { name: 'Password Reset', lastModified: 'Feb 18, 2026' },
  { name: 'New Lead', lastModified: 'Feb 15, 2026' },
];

const GET_COMPANY_FOR_BRANDING = `
  query GetCompanyForBranding {
    getMyProfile {
      contact
      company {
        companyName
        tradingName
        companyEmail
        companyPhone
        companyMobile
        unitNumber
        address
        suburb
        state
        postCode
        country
        invoicingEmail
        emailFrom
        emailFromName
        replyTo
        companyLogo
        businessType
      }
    }
  }
`;

function text(value) {
  return String(value || '').trim();
}

function asObject(value) {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  return typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function isPlaceholder(value) {
  const v = text(value);
  if (!v) return true;
  if (/your company/i.test(v)) return true;
  if (v === 'support@yourcompany.com' || v === 'noreply@yourcompany.com') return true;
  if (v === '+1 (555) 123-4567') return true;
  if (v === 'Empowering Your Success') return true;
  return false;
}

function filled(value, fallback) {
  if (isPlaceholder(value)) return text(fallback) || text(value);
  return text(value);
}

function toHexColor(value, fallback = '#7c3aed') {
  const v = text(value);
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return fallback;
}

function formatCompanyAddress(c = {}) {
  return [c.unitNumber, c.address, c.suburb, c.state, c.postCode, c.country]
    .map(text)
    .filter(Boolean)
    .join(', ');
}

function companyFallbacks(company, form = {}) {
  const c = { ...(company || {}), ...(form || {}) };
  const name = filled(c.tradingName, c.companyName);
  const email = filled(c.invoicingEmail, filled(c.companyEmail, c.emailFrom));
  const year = new Date().getFullYear();
  return {
    brandName: name,
    tagline: text(c.businessType),
    primaryLogo: text(c.companyLogo),
    emailLogo: text(c.companyLogo),
    supportEmail: email,
    contactNumber: filled(c.companyPhone, filled(c.companyMobile, c.contact)),
    address: formatCompanyAddress(c),
    fromName: filled(c.emailFromName, name),
    fromEmail: filled(c.emailFrom, email),
    replyTo: filled(c.replyTo, email),
    footerText: name ? `© ${year} ${name}. All rights reserved.` : '',
  };
}

function mergeSection(defaults, saved, fallbacks = {}) {
  const raw = asObject(saved);
  const merged = { ...defaults, ...raw };
  Object.entries(fallbacks).forEach(([key, fallback]) => {
    if (!Object.prototype.hasOwnProperty.call(merged, key)) return;
    if (typeof defaults[key] === 'boolean') return;
    if (defaults[key] && typeof defaults[key] === 'object') return;
    merged[key] = filled(raw[key], fallback) || defaults[key] || '';
  });
  return merged;
}

// Merge a backend config record onto the defaults so every field is defined.
function mergeWhiteLabel(record, fallbacks = {}) {
  const parsed = record || {};
  return {
    brand: mergeSection(DEFAULTS.brand, parsed.brand, {
      brandName: fallbacks.brandName,
      tagline: fallbacks.tagline,
      primaryLogo: fallbacks.primaryLogo,
    }),
    email: mergeSection(DEFAULTS.email, parsed.email, {
      emailLogo: fallbacks.emailLogo || fallbacks.primaryLogo,
      supportEmail: fallbacks.supportEmail,
      contactNumber: fallbacks.contactNumber,
      address: fallbacks.address,
      fromName: fallbacks.fromName,
      fromEmail: fallbacks.fromEmail,
      replyTo: fallbacks.replyTo,
      footerText: fallbacks.footerText,
    }),
    login: mergeSection(DEFAULTS.login, parsed.login),
  };
}

function logoSrc(value) {
  return resolveUploadUrl(value) || text(value);
}

function readUserRoles() {
  try {
    const roles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    // Roles may be stored in display form ("Super Admin") or constant form
    // ("SUPER_ADMIN"). Normalize both to underscore form so MODIFY_ROLES matches.
    return Array.isArray(roles)
      ? roles.map((r) => String(r).toUpperCase().replace(/[\s-]+/g, '_'))
      : [];
  } catch {
    return [];
  }
}

// Only Super Admin / Franchise Owner (mapped to ADMIN) may modify branding.
const MODIFY_ROLES = ['SUPER_ADMIN', 'SOFTWARE_ADMIN', 'ADMIN', 'FRANCHISE_OWNER'];

// ─── Small shared bits ──────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      className={`acct-toggle ${checked ? 'on' : ''}`}
      onClick={disabled ? undefined : onChange}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className="acct-toggle-knob" />
    </button>
  );
}

function ImageUpload({ label, hint, value, onChange, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Upload to S3 and store the hosted URL so the logo renders everywhere
  // (previews, invoice PDF, and emails). Falls back to a local data-URL when
  // S3 isn't reachable/configured, so the feature still works in dev.
  const handleFile = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) return;

    const storeAsDataUrl = () => {
      const reader = new FileReader();
      reader.onload = () => onChange(String(reader.result || ''));
      reader.readAsDataURL(file);
    };

    try {
      setUploading(true);
      const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
        files: [{ fileName: file.name, contentType: file.type || 'image/png' }],
      });
      const target = uploadData?.generateUrlForImageUploadClient?.[0];
      if (target?.url && target?.key) {
        await axios.put(target.url, file, {
          headers: { 'Content-Type': file.type || 'image/png' },
        });
        const publicUrl = resolveUploadUrl(target.key);
        if (publicUrl) {
          onChange(publicUrl);
          return;
        }
      }
      storeAsDataUrl();
    } catch (e) {
      console.error('Logo upload failed, storing inline copy instead', e);
      storeAsDataUrl();
    } finally {
      setUploading(false);
    }
  };

  const previewSrc = resolveUploadUrl(value) || value;

  return (
    <div className="wl-upload">
      <span className="st-field-label">{label}</span>
      <button
        type="button"
        className="wl-upload-box"
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        disabled={disabled || uploading}
      >
        {uploading ? (
          <span className="wl-upload-placeholder">
            <span>Uploading…</span>
          </span>
        ) : previewSrc ? (
          <img src={previewSrc} alt={label} className="wl-upload-preview" />
        ) : (
          <span className="wl-upload-placeholder">
            <FaUpload />
            <span>Click to upload</span>
          </span>
        )}
      </button>
      {hint ? <span className="st-help-text">{hint}</span> : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
}

function ColorField({ label, value, onChange, disabled }) {
  const hex = toHexColor(value);
  return (
    <label className="st-field">
      <span className="st-field-label">{label}</span>
      <div className="wl-color-row">
        <input
          type="color"
          className="wl-color-swatch"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <input
          className="st-field-input"
          value={value || hex}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </label>
  );
}

function ToggleRow({ title, subtitle, checked, onChange, disabled }) {
  return (
    <div className="st-pref-row">
      <div className="st-pref-text">
        <div className="st-pref-title">{title}</div>
        {subtitle ? <div className="st-pref-sub">{subtitle}</div> : null}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────
function OverviewView({ canModify, onNavigate }) {
  const cards = [
    { id: 'overview', icon: <FaEye />, title: 'Overview Dashboard', sub: 'View all white-label settings and status' },
    { id: 'brand', icon: <FaPalette />, title: 'Brand Settings', sub: 'Configure logo, colors, and branding' },
    { id: 'email', icon: <FaEnvelope />, title: 'Email Branding', sub: 'Customize email templates and branding' },
    { id: 'login', icon: <FaLock />, title: 'Login Customization', sub: 'Customize login page appearance' },
  ];

  return (
    <section className="acct-card st-card">
      <div className="wl-overview-head">
        <span className="st-card-icon"><FaPalette /></span>
        <div>
          <h2 className="wl-overview-title">White-Label Configuration</h2>
          <p className="wl-overview-sub">Customize your brand identity and visual appearance</p>
        </div>
      </div>

      <div className="st-card-body">
        <div className="wl-rbac">
          <FaInfoCircle className="wl-rbac-icon" />
          <span>
            <strong>Role-Based Access</strong> — Only Super Admin and Franchise Owner can modify
            these settings. Branch Managers have view-only access.
          </span>
        </div>

        <div className="wl-overview-grid">
          {cards.map((c) => (
            <button
              key={c.id}
              type="button"
              className="wl-overview-card"
              onClick={() => onNavigate(c.id === 'overview' ? 'brand' : c.id)}
            >
              <span className="wl-overview-card-icon">{c.icon}</span>
              <span className="wl-overview-card-text">
                <span className="wl-overview-card-title">{c.title}</span>
                <span className="wl-overview-card-sub">{c.sub}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="st-form-actions">
          <button type="button" className="acct-btn-secondary" onClick={() => onNavigate('brand')}>
            View All Settings
          </button>
          <button
            type="button"
            className="acct-btn-primary"
            style={{ marginLeft: 10 }}
            onClick={() => onNavigate('brand')}
            disabled={!canModify}
          >
            Configure Branding
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Brand Settings ──────────────────────────────────────────────────────────
function BrandView({ value, update, canModify, onSave, onReset, saved, saving }) {
  return (
    <>
      <section className="acct-card st-card">
        <h2 className="st-card-title">
          <span className="st-card-icon"><FaPalette /></span>
          Logo &amp; Visual Identity
        </h2>
        <div className="st-card-body">
          <p className="wl-section-sub">Upload your company logos and brand assets</p>

          <div className="wl-upload-grid">
            <ImageUpload
              label="Primary Logo"
              hint="PNG or SVG, max 2MB"
              value={value.primaryLogo}
              onChange={(v) => update({ primaryLogo: v })}
              disabled={!canModify}
            />
            <ImageUpload
              label="Dark Mode Logo"
              value={value.darkLogo}
              onChange={(v) => update({ darkLogo: v })}
              disabled={!canModify}
            />
            <ImageUpload
              label="Favicon"
              value={value.favicon}
              onChange={(v) => update({ favicon: v })}
              disabled={!canModify}
            />
          </div>

          <div className="st-form-grid">
            <label className="st-field">
              <span className="st-field-label">Brand Name</span>
              <input
                className="st-field-input"
                value={value.brandName}
                onChange={(e) => update({ brandName: e.target.value })}
                disabled={!canModify}
              />
            </label>
            <label className="st-field">
              <span className="st-field-label">Company Tagline</span>
              <input
                className="st-field-input"
                value={value.tagline}
                onChange={(e) => update({ tagline: e.target.value })}
                disabled={!canModify}
              />
            </label>
          </div>

          <div className="wl-preview-box">
            <span className="wl-preview-label">Preview</span>
            <div className="wl-brand-preview">
              <span
                className="wl-brand-preview-logo"
                style={{ background: value.primaryColor }}
              >
                {value.primaryLogo ? (
                  <img src={logoSrc(value.primaryLogo)} alt="logo" />
                ) : (
                  value.brandName.slice(0, 1).toUpperCase()
                )}
              </span>
              <span>
                <span className="wl-brand-preview-name">{value.brandName}</span>
                <span className="wl-brand-preview-tag">{value.tagline}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="wl-two-col">
        <section className="acct-card st-card">
          <h2 className="st-card-title">
            <span className="st-card-icon"><FaPalette /></span>
            Brand Colors
          </h2>
          <div className="st-card-body">
            <p className="wl-section-sub">Define your brand color palette</p>
            <div className="wl-color-stack">
              <ColorField label="Primary Color" value={value.primaryColor} onChange={(v) => update({ primaryColor: v })} disabled={!canModify} />
              <ColorField label="Secondary Color" value={value.secondaryColor} onChange={(v) => update({ secondaryColor: v })} disabled={!canModify} />
              <ColorField label="Accent Color" value={value.accentColor} onChange={(v) => update({ accentColor: v })} disabled={!canModify} />
            </div>

            <div className="wl-preview-box">
              <span className="wl-preview-label">Live Preview</span>
              <div className="wl-color-preview">
                <button type="button" className="wl-sample-btn" style={{ background: value.primaryColor }}>
                  Primary Button
                </button>
                <div className="wl-sample-card" style={{ background: value.accentColor }}>
                  Featured Card
                  <span>With primary color background</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="acct-card st-card">
          <h2 className="st-card-title">
            <span className="st-card-icon"><FaEye /></span>
            Remove Platform Branding
          </h2>
          <div className="st-card-body">
            <p className="wl-section-sub">Hide default SaaS branding elements</p>
            <ToggleRow
              title="Remove SaaS Name from Footer"
              subtitle="Hide platform name in footer area"
              checked={value.removeSaasName}
              onChange={() => update({ removeSaasName: !value.removeSaasName })}
              disabled={!canModify}
            />
            <ToggleRow
              title="Remove SaaS Logo from Login Page"
              subtitle="Use only your branding on login"
              checked={value.removeSaasLogo}
              onChange={() => update({ removeSaasLogo: !value.removeSaasLogo })}
              disabled={!canModify}
            />
            <ToggleRow
              title='Hide "Powered By" Footer'
              subtitle="Remove attribution footer text"
              checked={value.hidePoweredBy}
              onChange={() => update({ hidePoweredBy: !value.hidePoweredBy })}
              disabled={!canModify}
            />

            <div className="wl-preview-box">
              <span className="wl-preview-label">Preview</span>
              <div className="wl-powered-preview">
                {value.hidePoweredBy ? <span>&nbsp;</span> : <span>Powered by SaaS CRM</span>}
              </div>
            </div>
          </div>
        </section>
      </div>

      <SaveBar canModify={canModify} onSave={onSave} onReset={onReset} saved={saved} saving={saving} saveLabel="Save Changes" />
    </>
  );
}

function formatTemplateDate(value) {
  if (text(value)) return text(value);
  return new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function savedTemplates(branding) {
  return asObject(branding?.templates);
}

function defaultTemplateCopy(name, branding) {
  const brand = branding.brandName || 'Your company';
  switch (name) {
    case 'Deal Won':
      return {
        title: 'Deal won',
        greeting: 'Hi team,',
        body: `Great news — a deal was marked as won for ${brand}.`,
        rows: [
          ['Account', 'Acme Facilities'],
          ['Value', '$12,400.00'],
        ],
        cta: 'Open pipeline',
      };
    case 'SLA Breach':
      return {
        title: 'SLA breach alert',
        greeting: 'Hello,',
        body: `A job for ${brand} has exceeded its SLA window and needs attention.`,
        rows: [
          ['Job', 'Weekly clean — Level 3'],
          ['Overdue', '2 hours'],
        ],
        cta: 'Review job',
      };
    case 'Password Reset':
      return {
        title: 'Reset your password',
        greeting: 'Hello,',
        body: `We received a request to reset your ${brand} password.`,
        rows: [
          ['Expires', '30 minutes'],
          ['Requested', 'Just now'],
        ],
        cta: 'Reset password',
      };
    case 'New Lead':
      return {
        title: 'New lead received',
        greeting: 'Hi team,',
        body: `A new enquiry arrived for ${brand}.`,
        rows: [
          ['Name', 'Jordan Lee'],
          ['Service', 'Office cleaning'],
        ],
        cta: 'View lead',
      };
    default:
      return {
        title: 'Invoice #INV-2026-001',
        greeting: 'Dear Customer,',
        body: 'Your invoice is ready. Please find the details below.',
        rows: [
          ['Amount Due', '$1,299.00'],
          ['Due Date', 'Mar 15, 2026'],
        ],
        cta: 'View Invoice',
      };
  }
}

function resolveTemplateCopy(name, branding) {
  const base = defaultTemplateCopy(name, branding);
  const custom = savedTemplates(branding)[name];
  if (!custom || typeof custom !== 'object') return base;
  return {
    ...base,
    title: text(custom.title) || base.title,
    greeting: text(custom.greeting) || base.greeting,
    body: text(custom.body) || base.body,
    cta: text(custom.cta) || base.cta,
  };
}

function EmailBrandedPreview({ branding, templateName, onLogoError }) {
  const copy = resolveTemplateCopy(templateName, branding);
  const headerColor = toHexColor(branding.headerColor);
  const logo = logoSrc(branding.emailLogo);
  return (
    <div className="wl-invoice">
      <div className="wl-invoice-header" style={{ background: headerColor }}>
        {logo ? (
          <img src={logo} alt="" onError={onLogoError} />
        ) : (
          <span className="wl-invoice-header-name">{branding.brandName || 'Logo'}</span>
        )}
      </div>
      <div className="wl-invoice-body">
        <p className="wl-invoice-from">
          From {branding.fromName || branding.brandName || 'Company'}
          {branding.fromEmail ? ` · ${branding.fromEmail}` : ''}
        </p>
        <p className="wl-invoice-title">{copy.title}</p>
        <p>{copy.greeting}</p>
        <p>{copy.body}</p>
        {copy.rows.map(([label, val]) => (
          <div key={label} className="wl-invoice-amount">
            <span>{label}:</span>
            <strong>{val}</strong>
          </div>
        ))}
        <button
          type="button"
          className="wl-sample-btn"
          style={{ background: headerColor, width: '100%' }}
        >
          {copy.cta}
        </button>
        <div className="wl-invoice-footer">
          {branding.footerText ? <p>{branding.footerText}</p> : null}
          {branding.supportEmail ? <p>Email: {branding.supportEmail}</p> : null}
          {branding.contactNumber ? <p>Phone: {branding.contactNumber}</p> : null}
          {branding.address ? <p>{branding.address}</p> : null}
        </div>
      </div>
    </div>
  );
}

// ─── Email Branding ──────────────────────────────────────────────────────────
function EmailView({
  value,
  update,
  canModify,
  onSave,
  onReset,
  saved,
  saving,
  fallbacks,
  brandName,
  primaryColor,
}) {
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editDraft, setEditDraft] = useState({
    title: '',
    greeting: '',
    body: '',
    cta: '',
  });
  const [logoBroken, setLogoBroken] = useState(false);

  const shown = useMemo(() => {
    const headerColor = toHexColor(
      value.headerColor || primaryColor,
      DEFAULTS.email.headerColor,
    );
    return {
      ...value,
      emailLogo: logoBroken ? '' : filled(value.emailLogo, fallbacks.emailLogo),
      headerColor,
      footerText: filled(value.footerText, fallbacks.footerText),
      supportEmail: filled(value.supportEmail, fallbacks.supportEmail),
      contactNumber: filled(value.contactNumber, fallbacks.contactNumber),
      address: filled(value.address, fallbacks.address),
      fromName: filled(value.fromName, fallbacks.fromName || brandName),
      fromEmail: filled(value.fromEmail, fallbacks.fromEmail),
      replyTo: filled(value.replyTo, fallbacks.replyTo),
      brandName: brandName || fallbacks.brandName,
      templates: savedTemplates(value),
    };
  }, [value, fallbacks, brandName, primaryColor, logoBroken]);

  const openEdit = (name) => {
    const copy = resolveTemplateCopy(name, shown);
    setPreviewTemplate(null);
    setEditDraft({
      title: copy.title,
      greeting: copy.greeting,
      body: copy.body,
      cta: copy.cta,
    });
    setEditTemplate(name);
  };

  const saveEdit = async () => {
    if (!editTemplate || !canModify) return;
    const templates = {
      ...savedTemplates(value),
      [editTemplate]: {
        title: text(editDraft.title),
        greeting: text(editDraft.greeting),
        body: text(editDraft.body),
        cta: text(editDraft.cta),
        lastModified: formatTemplateDate(),
      },
    };
    update({ templates });
    await onSave({ email: { ...value, templates } });
    setEditTemplate(null);
  };

  const editPreviewBranding = editTemplate
    ? {
        ...shown,
        templates: {
          ...savedTemplates(shown),
          [editTemplate]: editDraft,
        },
      }
    : shown;

  return (
    <>
      <section className="acct-card st-card">
        <h2 className="st-card-title">
          <span className="st-card-icon"><FaEnvelope /></span>
          Invoice Header Branding
        </h2>
        <div className="st-card-body">
          <p className="wl-section-sub">
            Customize the header and footer of system emails. Empty fields use
            General company settings.
          </p>

          <div className="wl-two-col">
            <div className="wl-email-form">
              <ImageUpload
                label="Email Logo"
                hint="PNG or JPG, 400x100px recommended. Defaults to the General company logo."
                value={shown.emailLogo}
                onChange={(v) => {
                  setLogoBroken(false);
                  update({ emailLogo: v });
                }}
                disabled={!canModify}
              />
              <ColorField
                label="Header Background Color"
                value={shown.headerColor}
                onChange={(v) => update({ headerColor: v })}
                disabled={!canModify}
              />
              <label className="st-field">
                <span className="st-field-label">Footer Text</span>
                <input className="st-field-input" value={shown.footerText} onChange={(e) => update({ footerText: e.target.value })} disabled={!canModify} />
              </label>
              <label className="st-field">
                <span className="st-field-label">Support Email</span>
                <input className="st-field-input" value={shown.supportEmail} onChange={(e) => update({ supportEmail: e.target.value })} disabled={!canModify} />
              </label>
              <label className="st-field">
                <span className="st-field-label">Contact Number</span>
                <input className="st-field-input" value={shown.contactNumber} onChange={(e) => update({ contactNumber: e.target.value })} disabled={!canModify} />
              </label>
              <label className="st-field">
                <span className="st-field-label">Address</span>
                <input className="st-field-input" value={shown.address} onChange={(e) => update({ address: e.target.value })} disabled={!canModify} />
              </label>
            </div>

            <div className="wl-email-preview">
              <span className="wl-preview-label">Live email preview</span>
              <EmailBrandedPreview
                branding={shown}
                templateName="Invoice Sent"
                onLogoError={() => setLogoBroken(true)}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="acct-card st-card">
        <h2 className="st-card-title">
          <span className="st-card-icon"><FaEnvelope /></span>
          Email Template Branding
        </h2>
        <div className="st-card-body">
          <p className="wl-section-sub">Edit and preview how system emails look with your header branding</p>
          <div className="wl-tmpl-table">
            <div className="wl-tmpl-head">
              <span>Template Name</span>
              <span>Status</span>
              <span>Last Modified</span>
              <span>Actions</span>
            </div>
            {EMAIL_TEMPLATES.map((t) => (
              <div key={t.name} className="wl-tmpl-row">
                <span className="wl-tmpl-name"><FaEnvelope /> {t.name}</span>
                <span><span className="cd-badge cd-badge-active">Active</span></span>
                <span className="wl-tmpl-date">
                  {savedTemplates(value)[t.name]?.lastModified || t.lastModified}
                </span>
                <span className="wl-tmpl-actions">
                  <button
                    type="button"
                    className="cd-action-btn"
                    onClick={() => openEdit(t.name)}
                    disabled={!canModify}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    type="button"
                    className="cd-action-btn"
                    onClick={() => {
                      setEditTemplate(null);
                      setPreviewTemplate(t.name);
                    }}
                  >
                    <FaEye /> Preview
                  </button>
                  <button
                    type="button"
                    className="cd-action-btn"
                    onClick={onReset}
                    disabled={!canModify}
                  >
                    <FaUndo /> Reset
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="acct-card st-card">
        <h2 className="st-card-title">
          <span className="st-card-icon"><FaEnvelope /></span>
          Additional Email Settings
        </h2>
        <div className="st-card-body">
          <div className="st-form-grid">
            <label className="st-field">
              <span className="st-field-label">Default From Name</span>
              <input className="st-field-input" value={shown.fromName} onChange={(e) => update({ fromName: e.target.value })} disabled={!canModify} />
            </label>
            <label className="st-field">
              <span className="st-field-label">Default From Email</span>
              <input className="st-field-input" value={shown.fromEmail} onChange={(e) => update({ fromEmail: e.target.value })} disabled={!canModify} />
            </label>
            <label className="st-field">
              <span className="st-field-label">Reply-To Email</span>
              <input className="st-field-input" value={shown.replyTo} onChange={(e) => update({ replyTo: e.target.value })} disabled={!canModify} />
            </label>
            <label className="st-field">
              <span className="st-field-label">BCC Email (Optional)</span>
              <input className="st-field-input" value={value.bcc || ''} onChange={(e) => update({ bcc: e.target.value })} disabled={!canModify} />
            </label>
          </div>
        </div>
      </section>

      {previewTemplate ? (
        <div
          className="wl-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${previewTemplate} preview`}
          onClick={() => setPreviewTemplate(null)}
        >
          <div
            className="wl-preview-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wl-preview-modal-head">
              <h3>{previewTemplate} preview</h3>
              <button
                type="button"
                className="wl-preview-close"
                aria-label="Close preview"
                onClick={() => setPreviewTemplate(null)}
              >
                <FaTimes />
              </button>
            </div>
            <p className="wl-section-sub">
              This template uses your Invoice Header Branding and General company details.
            </p>
            <EmailBrandedPreview
              branding={shown}
              templateName={previewTemplate}
              onLogoError={() => setLogoBroken(true)}
            />
          </div>
        </div>
      ) : null}

      {editTemplate ? (
        <div
          className="wl-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`Edit ${editTemplate}`}
          onClick={() => setEditTemplate(null)}
        >
          <div
            className="wl-preview-modal-card wl-edit-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wl-preview-modal-head">
              <h3>Edit {editTemplate}</h3>
              <button
                type="button"
                className="wl-preview-close"
                aria-label="Close editor"
                onClick={() => setEditTemplate(null)}
              >
                <FaTimes />
              </button>
            </div>
            <p className="wl-section-sub">
              Update the subject, greeting, body, and button for this email. Header
              branding still comes from Invoice Header Branding.
            </p>
            <div className="wl-edit-fields">
              <label className="st-field">
                <span className="st-field-label">Subject / title</span>
                <input
                  className="st-field-input"
                  value={editDraft.title}
                  onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                  disabled={!canModify}
                />
              </label>
              <label className="st-field">
                <span className="st-field-label">Greeting</span>
                <input
                  className="st-field-input"
                  value={editDraft.greeting}
                  onChange={(e) => setEditDraft((d) => ({ ...d, greeting: e.target.value }))}
                  disabled={!canModify}
                />
              </label>
              <label className="st-field">
                <span className="st-field-label">Body</span>
                <textarea
                  className="st-field-input wl-edit-body"
                  value={editDraft.body}
                  onChange={(e) => setEditDraft((d) => ({ ...d, body: e.target.value }))}
                  disabled={!canModify}
                />
              </label>
              <label className="st-field">
                <span className="st-field-label">Button label</span>
                <input
                  className="st-field-input"
                  value={editDraft.cta}
                  onChange={(e) => setEditDraft((d) => ({ ...d, cta: e.target.value }))}
                  disabled={!canModify}
                />
              </label>
            </div>
            <span className="wl-preview-label">Live preview</span>
            <EmailBrandedPreview
              branding={editPreviewBranding}
              templateName={editTemplate}
              onLogoError={() => setLogoBroken(true)}
            />
            <div className="wl-edit-actions">
              <button
                type="button"
                className="acct-btn-secondary"
                onClick={() => setEditTemplate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="acct-btn-primary"
                onClick={saveEdit}
                disabled={!canModify || saving}
              >
                {saving ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <SaveBar canModify={canModify} onSave={onSave} onReset={onReset} saved={saved} saving={saving} saveLabel="Save Email Branding" />
    </>
  );
}

// ─── Login Customization ─────────────────────────────────────────────────────
function LoginView({ value, update, canModify, onSave, onReset, saved, saving }) {
  return (
    <>
      <div className="wl-two-col">
        <section className="acct-card st-card">
          <h2 className="st-card-title">
            <span className="st-card-icon"><FaLock /></span>
            Login Page Branding
          </h2>
          <div className="st-card-body">
            <p className="wl-section-sub">Configure login page visual elements</p>
            <ImageUpload
              label="Background Image"
              hint="Recommended: 1920x1080px"
              value={value.bgImage}
              onChange={(v) => update({ bgImage: v })}
              disabled={!canModify}
            />
            <ColorField label="Background Color" value={value.bgColor} onChange={(v) => update({ bgColor: v })} disabled={!canModify} />
            <span className="st-help-text wl-color-help">Used as fallback if no background image is set</span>
            <label className="st-field">
              <span className="st-field-label">Login Headline Text</span>
              <input className="st-field-input" value={value.headline} onChange={(e) => update({ headline: e.target.value })} disabled={!canModify} />
            </label>
            <label className="st-field">
              <span className="st-field-label">Subtext</span>
              <input className="st-field-input" value={value.subtext} onChange={(e) => update({ subtext: e.target.value })} disabled={!canModify} />
            </label>
            <label className="st-field">
              <span className="st-field-label">Button Label</span>
              <input className="st-field-input" value={value.buttonLabel} onChange={(e) => update({ buttonLabel: e.target.value })} disabled={!canModify} />
            </label>

            <div className="wl-boxed-toggle">
              <ToggleRow
                title="Enable Custom CSS"
                subtitle="Add advanced styling with custom CSS"
                checked={value.enableCustomCss}
                onChange={() => update({ enableCustomCss: !value.enableCustomCss })}
                disabled={!canModify}
              />
            </div>
            {value.enableCustomCss ? (
              <label className="st-field">
                <span className="st-field-label">Custom CSS</span>
                <textarea
                  className="st-field-input"
                  rows={4}
                  value={value.customCss}
                  onChange={(e) => update({ customCss: e.target.value })}
                  disabled={!canModify}
                />
              </label>
            ) : null}

            <div className="wl-login-actions">
              <button type="button" className="acct-btn-primary wl-login-save" onClick={onSave} disabled={!canModify || saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" className="acct-btn-secondary wl-login-preview-btn">
                <FaEye style={{ marginRight: 6 }} /> Preview
              </button>
            </div>
          </div>
        </section>

        <section className="acct-card st-card">
          <h2 className="st-card-title">
            <span className="st-card-icon"><FaEye /></span>
            Live Preview
          </h2>
          <div className="st-card-body">
            <p className="wl-section-sub">See how your login page will look</p>
            <div
              className="wl-login-preview"
              style={{
                background: value.bgImage
                  ? `url(${resolveUploadUrl(value.bgImage) || value.bgImage}) center/cover`
                  : value.bgColor,
              }}
            >
              <div className="wl-login-card">
                <span className="wl-login-eye"><FaEye /></span>
                <p className="wl-login-headline">{value.headline}</p>
                <p className="wl-login-subtext">{value.subtext}</p>
                <label className="st-field">
                  <span className="st-field-label">Email</span>
                  <input className="st-field-input" placeholder="you@example.com" disabled />
                </label>
                <label className="st-field">
                  <span className="st-field-label">Password</span>
                  <input className="st-field-input" type="password" placeholder="••••••••" disabled />
                </label>
                <div className="wl-login-remember">
                  <label className="wl-login-checkbox">
                    <input type="checkbox" disabled /> Remember me
                  </label>
                  <span className="wl-login-forgot">Forgot password?</span>
                </div>
                <button type="button" className="wl-sample-btn" style={{ background: '#7c3aed', width: '100%' }}>
                  {value.buttonLabel}
                </button>
                <p className="wl-login-signup">
                  Don&apos;t have an account? <span>Sign up</span>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="acct-card st-card">
        <h2 className="st-card-title">
          <span className="st-card-icon"><FaLock /></span>
          Additional Settings
        </h2>
        <div className="st-card-body">
          <p className="wl-section-sub">Configure additional login page options</p>
          <div className="wl-toggle-grid">
            <div className="wl-boxed-toggle">
              <ToggleRow title="Show Social Login" subtitle="Display Google/Microsoft login options" checked={value.showSocialLogin} onChange={() => update({ showSocialLogin: !value.showSocialLogin })} disabled={!canModify} />
            </div>
            <div className="wl-boxed-toggle">
              <ToggleRow title="Enable Two-Factor Authentication" subtitle="Require 2FA for all users" checked={value.enable2fa} onChange={() => update({ enable2fa: !value.enable2fa })} disabled={!canModify} />
            </div>
            <div className="wl-boxed-toggle">
              <ToggleRow title="Show Language Selector" subtitle="Allow users to choose language" checked={value.showLanguage} onChange={() => update({ showLanguage: !value.showLanguage })} disabled={!canModify} />
            </div>
            <div className="wl-boxed-toggle">
              <ToggleRow title="Custom Terms & Privacy Links" subtitle="Display your T&C and privacy policy" checked={value.customTerms} onChange={() => update({ customTerms: !value.customTerms })} disabled={!canModify} />
            </div>
          </div>
        </div>
      </section>

      <SaveBar canModify={canModify} onSave={onSave} onReset={onReset} saved={saved} saving={saving} saveLabel="Save Changes" />
    </>
  );
}

function SaveBar({ canModify, onSave, onReset, saved, saving, saveLabel }) {
  return (
    <div className="st-card wl-savebar">
      {saving ? (
        <span className="st-message">Saving…</span>
      ) : saved ? (
        <span className="st-message st-message--success">Saved.</span>
      ) : (
        <span />
      )}
      <div>
        <button type="button" className="acct-btn-secondary" onClick={onReset} disabled={!canModify || saving}>
          Reset to Default
        </button>
        <button type="button" className="acct-btn-primary" style={{ marginLeft: 10 }} onClick={onSave} disabled={!canModify || saving}>
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

const WL_VIEWS = ['overview', 'brand', 'email', 'login'];

// ─── Main ────────────────────────────────────────────────────────────────────
export default function WhiteLabelSettings({ company, companyForm } = {}) {
  const { get, patch } = useAdminPageSearch();
  const wlParam = get('wl');
  const view = WL_VIEWS.includes(wlParam) ? wlParam : 'overview';
  const setView = (id) => patch({ wl: id === 'overview' ? '' : id });

  const [rawConfig, setRawConfig] = useState(null);
  const [fetchedCompany, setFetchedCompany] = useState(null);
  const [fetchedContact, setFetchedContact] = useState('');
  const [data, setData] = useState(() => mergeWhiteLabel(null));
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);

  const resolvedCompany = company || fetchedCompany;
  const fallbacks = useMemo(
    () =>
      companyFallbacks(resolvedCompany, {
        ...(companyForm || {}),
        contact: fetchedContact,
      }),
    [resolvedCompany, companyForm, fetchedContact],
  );

  const canModify = useMemo(() => {
    const roles = readUserRoles();
    if (roles.length === 0) return true;
    return roles.some((r) => MODIFY_ROLES.includes(r));
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await graphqlRequest(GET_WHITE_LABEL);
        if (active) setRawConfig(res?.getWhiteLabelConfig || null);
      } catch (e) {
        console.error('Failed to load white-label settings', e);
        if (active) setRawConfig(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (company?.id) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await graphqlRequest(GET_COMPANY_FOR_BRANDING);
        if (!active) return;
        setFetchedCompany(res?.getMyProfile?.company || null);
        setFetchedContact(res?.getMyProfile?.contact || '');
      } catch (e) {
        try {
          const res = await graphqlRequest(`
            query GetCompanyForBrandingCore {
              getMyProfile {
                contact
                company {
                  companyName
                  companyEmail
                  companyPhone
                  address
                  suburb
                  state
                  postCode
                  country
                  companyLogo
                }
              }
            }
          `);
          if (!active) return;
          setFetchedCompany(res?.getMyProfile?.company || null);
          setFetchedContact(res?.getMyProfile?.contact || '');
        } catch (inner) {
          console.warn('Failed to load company for email branding', inner || e);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [company?.id]);

  useEffect(() => {
    if (dirtyRef.current) return;
    setData(mergeWhiteLabel(rawConfig, fallbacks));
  }, [rawConfig, fallbacks]);

  const updateSection = (section) => (patchSection) => {
    dirtyRef.current = true;
    setSaved(false);
    setData((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...patchSection },
    }));
  };

  const persist = async (override) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = mergeWhiteLabel(
        {
          brand: override?.brand || data.brand,
          email: override?.email || data.email,
          login: override?.login || data.login,
        },
        fallbacks,
      );
      const res = await graphqlRequest(UPDATE_WHITE_LABEL, {
        input: {
          brand: payload.brand,
          email: payload.email,
          login: payload.login,
        },
      });
      const next = res?.updateWhiteLabelConfig
        ? mergeWhiteLabel(res.updateWhiteLabelConfig, fallbacks)
        : payload;
      setRawConfig(res?.updateWhiteLabelConfig || rawConfig);
      dirtyRef.current = false;
      setData(next);
      applyBrandingFromConfig(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('Failed to save white-label settings', e);
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (section) => () => {
    dirtyRef.current = false;
    const fresh = mergeWhiteLabel(null, fallbacks);
    setData((prev) => ({ ...prev, [section]: fresh[section] }));
    setSaved(false);
  };

  return (
    <div className="st-layout wl-layout">
      {view !== 'overview' && (
        <button type="button" className="wl-back" onClick={() => setView('overview')}>
          <FaArrowLeft /> Back to White-Label Overview
        </button>
      )}

      {view === 'overview' && <OverviewView canModify={canModify} onNavigate={setView} />}

      {view === 'brand' && (
        <BrandView
          value={data.brand}
          update={updateSection('brand')}
          canModify={canModify}
          onSave={persist}
          onReset={resetSection('brand')}
          saved={saved}
          saving={saving}
        />
      )}

      {view === 'email' && (
        <EmailView
          value={data.email}
          update={updateSection('email')}
          canModify={canModify}
          onSave={persist}
          onReset={resetSection('email')}
          saved={saved}
          saving={saving}
          fallbacks={fallbacks}
          brandName={data.brand.brandName}
          primaryColor={data.brand.primaryColor}
        />
      )}

      {view === 'login' && (
        <LoginView
          value={data.login}
          update={updateSection('login')}
          canModify={canModify}
          onSave={persist}
          onReset={resetSection('login')}
          saved={saved}
          saving={saving}
        />
      )}
    </div>
  );
}
